<template>
    <div class="py-4 container-fluid">
        <div class=" row">
            <div class="col-12">
                <div class="card">
                    <div class="card-header pb-0">
                        <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                            <span class="section-title mb-0">Historial de pagos</span>
                            <button v-if="effectiveUserId" type="button" class="btn btn-sm btn-warning" @click="addPaid">
                                <i class="fas fa-hand-holding-usd me-1"></i> Agregar pago
                            </button>
                        </div>

                        <!-- Buscador y filtros por fecha en la misma línea -->
                        <div class="row align-items-end">
                            <div class="col-md-4 mb-3">
                                <label for="searchQuery" class="mb-1">Buscar</label>
                                <input type="text" v-model="searchQuery" id="searchQuery" class="form-control"
                                    placeholder="Buscar observación..." @input="fetchData(null)" />
                            </div>
                            <div class="col-md-4 mb-3">
                                <label for="dateStart" class="mb-1">Fecha inicio</label>
                                <input type="date" v-model="dateStart" id="dateStart" class="form-control"
                                    @change="fetchData(null)" />
                            </div>
                            <div class="col-md-4 mb-3">
                                <label for="dateEnd" class="mb-1">Fecha fin</label>
                                <input type="date" v-model="dateEnd" id="dateEnd" class="form-control"
                                    @change="fetchData(null)" />
                            </div>
                        </div>

                        <!-- Nombre de la persona asignada al historial -->
                        <div class="row mt-2 mb-2" v-if="displayUserName">
                            <div class="col-12">
                                <p class="mb-0 text-sm font-weight-bold nombre-empleado-historial">{{ displayUserName }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="card-body px-3 pt-0 pb-2">
                        <div class="table-responsive p-0">
                            <table class="table align-items-center mb-0">
                                <thead>
                                    <tr>
                                        <th
                                            class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                                            style="padding-left: 3px;">
                                            Fecha</th>
                                        <th
                                            class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                            Total pagado</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                            Observación</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    <tr v-for="value in data" :key="value.id">
                                        <td class="align-middle text-sm text-start" style="padding-left: 3px;">
                                            <h6 class="mb-0 text-sm">{{ isValidDate(value.created_at) ? new
                                                Date(value.created_at).toLocaleDateString('es-ES') : 'Sin asignar' }}
                                            </h6>
                                        </td>
                                        <td class="align-middle text-center text-sm">
                                            <h6 class="mb-0 text-sm">{{
                                                parseFloat(value.total ?? 0).toLocaleString('es-ES', {
                                                    minimumFractionDigits: 2, maximumFractionDigits: 2
                                                }) }}</h6>
                                        </td>
                                        <td class="align-middle text-sm">
                                            <h6 class="d-flex px-2 py-1 mb-0 text-sm">{{ value.observation }}</h6>
                                        </td>
                                    </tr>

                                    <tr v-if="data.length === 0">
                                        <td colspan="3" class="text-center text-xxs">No hay registros disponibles</td>
                                    </tr>
                                </tbody>
                            </table>

                            <div class="pagination p-3">
                                <!-- Recorremos los links para generar los botones -->
                                <a v-for="link in links" :key="link.label" class="page-item cursor-pointer"
                                    @click="fetchData(link.url)" :disabled="!link.url || link.active"
                                    :class="{ 'active': link.label === currentPage.toString() }">
                                    {{ link.label }}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import { mapGetters } from 'vuex';
import userService from '../../../services/user.service';
import Swal from 'sweetalert2';

export default {
    name: 'ProjectsList',
    props: ['userId', 'userName'],
    data() {
        return {
            searchQuery: '',
            dateStart: '',
            dateEnd: '',
            userNameLoaded: '',
        };
    },
    computed: {
        /** userId puede llegar por prop o por $route.params (la ruta no tiene :userId en la URL) */
        effectiveUserId() {
            return this.userId ?? this.$route?.params?.userId ?? null;
        },
        /** Nombre a mostrar: primero el que viene por ruta/prop (desde la tabla), si no el cargado por API */
        displayUserName() {
            const fromParams = this.$route?.params?.userName ?? this.userName;
            return (fromParams && String(fromParams).trim()) || this.userNameLoaded || '';
        },
        ...mapGetters('productionPayments', ['allData', 'currentPage', 'lastPage', 'pagination', 'links']), // Mapea el getter allUsers
        currentPage() {
            return this.$store.state.productionPayments.currentPage; // Obtiene la pagina actual
        },
        lastPage() {
            return this.$store.state.productionPayments.lastPage; // Obtiene la última página
        },
        data() {
            return this.allData; // Retorna los empleados desde el store
        },
        links() {
            return this.$store.getters['productionPayments/links']; // Retorna los links
        },
    },
    methods: {
        async fetchData(pageUrl = null) {
            const params = {
                user_id: this.effectiveUserId,
                page: pageUrl ? new window.URL(pageUrl).searchParams.get("page") || 1 : 1
            };
            if (this.searchQuery) params.search = this.searchQuery;
            if (this.dateStart) params.date_start = this.dateStart;
            if (this.dateEnd) params.date_end = this.dateEnd;
            await this.$store.dispatch('productionPayments/fetchData', params);
        },
        isValidDate(date) {
            if (!date) {
                return false;
            }
            const parsedDate = new Date(date);
            return !isNaN(parsedDate.getTime());
        },
        async addPaid() {
            const userId = this.effectiveUserId;
            if (!userId) {
                Swal.fire("Aviso", "No hay empleado seleccionado.", "info");
                return;
            }
            const { value: formValues } = await Swal.fire({
                title: "Agregar Pago",
                html: `
                    <div>
                        <input id="swal-price" class="swal2-input mt-2" placeholder="Precio a abonar" style="width:100%;margin:0px">
                        <textarea id="swal-observation" class="swal2-input mt-2" placeholder="Observación" style="width:100%;margin:0px;height:80px;"></textarea>
                    </div>`,
                focusConfirm: false,
                showCancelButton: true,
                confirmButtonText: "Guardar",
                cancelButtonText: "Cancelar",
                preConfirm: () => {
                    const price = parseFloat(document.getElementById("swal-price").value.trim());
                    const observation = document.getElementById("swal-observation").value.trim();
                    if (isNaN(price) || price <= 0) {
                        Swal.showValidationMessage("El precio debe ser un número válido y positivo");
                        return false;
                    }
                    return { price, observation };
                },
            });
            if (formValues) {
                try {
                    const payment = {
                        id: 0,
                        production_user_id: userId,
                        total: formValues.price,
                        observation: formValues.observation,
                    };
                    const response = await this.$store.dispatch("productionPayments/save", payment);
                    if (response && response.status >= 200 && response.status < 300) {
                        Swal.fire("Éxito", "Pago registrado correctamente", "success");
                        this.fetchData();
                    } else {
                        throw new Error(response?.data?.message || "Error al registrar el pago");
                    }
                } catch (error) {
                    Swal.fire("Error", "No se pudo registrar el pago", "error");
                }
            }
        },
        async loadUserName() {
            const uid = this.effectiveUserId;
            if (!uid) {
                this.userNameLoaded = '';
                return;
            }
            try {
                const response = await userService.getUser(uid);
                const d = response?.data;
                this.userNameLoaded = (d?.name ?? d?.data?.name ?? (typeof d === 'string' ? d : '')) || '';
            } catch (e) {
                this.userNameLoaded = '';
            }
        },
    },
    mounted() {
        this.loadUserName();
        this.fetchData();
    },
    watch: {
        userId() {
            this.loadUserName();
        },
        '$route.params.userId'() {
            this.loadUserName();
        },
    },
};
</script>

<style scoped>
.nombre-empleado-historial {
    color: #1f2d5a;
}

.section-title {
    font-weight: 800;
    color: #1f2d5a;
    letter-spacing: 0.6px;
}

.section-title::after {
    content: "";
    display: block;
    width: 44px;
    height: 3px;
    margin-top: 6px;
    border-radius: 999px;
    background: #f6a500;
}

.pagination {
    display: flex;
    justify-content: center;
    margin-top: 20px;
}

.page-item {
    margin: 0 5px;
}

.active {
    font-weight: bold;
    /* Estilo para la página activa */
}
</style>